'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { Button, IconButton } from '../../actions/button/index.js';
import { Badge } from '../../data-display/badge/index.js';
import { NativeSelect } from '../../forms/form-field/index.js';
import { ConfirmDialog } from '../../overlays/confirm-dialog/index.js';
import { Avatar } from '../avatar/index.js';
const DEFAULT_MESSAGES = {
    you: 'You',
    invited: 'Invited',
    suspended: 'Suspended',
    presence: { online: 'Online', away: 'Away', busy: 'Busy', offline: 'Offline' },
    roleLabel: (name) => `Role for ${name}`,
    resend: 'Resend invite',
    resent: 'Invite sent again.',
    remove: (name) => `Remove ${name}`,
    revoke: (name) => `Revoke invite for ${name}`,
    removeTitle: (name) => `Remove ${name}?`,
    revokeTitle: (name) => `Revoke the invite for ${name}?`,
    removeDescription: 'They lose access straight away. You can invite them again later.',
    revokeDescription: 'The invite link stops working. You can invite them again later.',
    removeConfirm: 'Remove',
    revokeConfirm: 'Revoke invite',
    cancel: 'Cancel',
    failed: 'That did not work. Try again.',
};
function TrashGlyph(props) {
    return (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.75", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true, focusable: false, ...props, children: _jsx("path", { d: "M2.75 4.25h10.5M6.25 4.25V2.75h3.5v1.5M4.25 4.25l.6 9h6.3l.6-9M6.75 6.75v4M9.25 6.75v4" }) }));
}
function messageOf(error, fallback) {
    return error instanceof Error && error.message ? error.message : fallback;
}
/**
 * One member of a workspace, space or channel: avatar, name, email, role,
 * status (invited, active, suspended) and actions. Role changes, resends and
 * removals go through product callbacks; removal is confirmed first. Use
 * inside `MemberList`.
 */
export const MemberRow = forwardRef(function MemberRow({ name, email, avatarUrl, presence, meta, badges, isSelf = false, status = 'active', memberRole: roleProp, defaultMemberRole, roleOptions: roles = [], onMemberRoleChange: onRoleChange, canChangeRole, onResend, onRemove, canRemove, removeConsequences, actions, disabled = false, as: Tag = 'li', messages: messagesProp, className, ...rest }, ref) {
    const messages = { ...DEFAULT_MESSAGES, ...messagesProp };
    const [innerRole, setInnerRole] = useState(defaultMemberRole);
    const controlled = roleProp !== undefined;
    const role = controlled ? roleProp : innerRole;
    const [rolePending, setRolePending] = useState(false);
    const [resendPending, setResendPending] = useState(false);
    const [notice, setNotice] = useState(null);
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    const editable = (canChangeRole ?? (onRoleChange !== undefined && !isSelf)) && roles.length > 0;
    const removable = (canRemove ?? (onRemove !== undefined && !isSelf)) && onRemove !== undefined;
    const invited = status === 'invited';
    const roleLabel = roles.find((r) => r.value === role)?.label ?? role;
    const changeRole = async (next) => {
        const previous = role;
        if (!controlled)
            setInnerRole(next);
        setNotice(null);
        setRolePending(true);
        try {
            await onRoleChange?.(next);
        }
        catch (error) {
            if (!mounted.current)
                return;
            if (!controlled)
                setInnerRole(previous);
            setNotice({ tone: 'error', text: messageOf(error, messages.failed) });
        }
        finally {
            if (mounted.current)
                setRolePending(false);
        }
    };
    const resend = async () => {
        if (!onResend || resendPending)
            return;
        setNotice(null);
        setResendPending(true);
        try {
            await onResend();
            if (mounted.current)
                setNotice({ tone: 'done', text: messages.resent });
        }
        catch (error) {
            if (mounted.current)
                setNotice({ tone: 'error', text: messageOf(error, messages.failed) });
        }
        finally {
            if (mounted.current)
                setResendPending(false);
        }
    };
    return (_jsxs(Tag, { ref: ref, ...rest, className: cx('mn-member-row', className), "data-status": status, "data-self": isSelf || undefined, children: [_jsx(Avatar, { className: "mn-member-row__avatar", name: name, src: avatarUrl ?? null, size: "md", decorative: true, ...(presence ? { status: presence } : {}) }), _jsxs("div", { className: "mn-member-row__identity", children: [_jsxs("p", { className: "mn-member-row__name-line", children: [_jsx("span", { className: "mn-member-row__name", children: name }), presence ? (_jsxs("span", { className: "mn-visually-hidden", children: [" (", messages.presence[presence], ")"] })) : null, isSelf ? _jsxs("span", { className: "mn-member-row__self", children: ["(", messages.you, ")"] }) : null, status !== 'active' ? (_jsx(Badge, { size: "sm", tone: invited ? 'info' : 'warning', className: "mn-member-row__status", children: invited ? messages.invited : messages.suspended })) : null, badges] }), email ? _jsx("p", { className: "mn-member-row__email", children: email }) : null, meta ? _jsx("p", { className: "mn-member-row__meta", children: meta }) : null, _jsx("p", { className: "mn-member-row__notice", role: "status", children: notice?.tone === 'done' ? notice.text : null }), notice?.tone === 'error' ? (_jsx("p", { className: "mn-member-row__notice", role: "alert", "data-tone": "error", children: notice.text })) : null] }), _jsxs("div", { className: "mn-member-row__controls", children: [editable ? (_jsx(NativeSelect, { size: "sm", "aria-label": messages.roleLabel(name), value: role ?? '', options: roles, ...(role === undefined ? { placeholder: '' } : {}), disabled: disabled || rolePending, "aria-busy": rolePending || undefined, onChange: (e) => void changeRole(e.target.value), wrapperClassName: "mn-member-row__role-select" })) : roleLabel ? (_jsx("span", { className: "mn-member-row__role", children: roleLabel })) : null, invited && onResend ? (_jsx(Button, { size: "sm", variant: "ghost", loading: resendPending, disabled: disabled, onClick: resend, "aria-label": `${messages.resend}, ${name}`, children: messages.resend })) : null, actions, removable ? (_jsx(ConfirmDialog, { tone: "danger", title: invited ? messages.revokeTitle(name) : messages.removeTitle(name), description: invited ? messages.revokeDescription : messages.removeDescription, ...(removeConsequences ? { consequences: removeConsequences } : {}), confirmLabel: invited ? messages.revokeConfirm : messages.removeConfirm, cancelLabel: messages.cancel, onConfirm: () => onRemove?.(), trigger: _jsx(IconButton, { size: "sm", icon: _jsx(TrashGlyph, {}), label: invited ? messages.revoke(name) : messages.remove(name), disabled: disabled, className: "mn-member-row__remove" }) })) : null] })] }));
});
/** The list that holds MemberRows, so screen readers announce "list, 12 items". */
export const MemberList = forwardRef(function MemberList({ className, ...rest }, ref) {
    return _jsx("ul", { ref: ref, ...rest, className: cx('mn-member-list', className) });
});
//# sourceMappingURL=member-row.js.map